<template>
    <div>
        <v-container
            v-if="!canBeEditable"
            class="pa-0 digital-header"
            fluid
        >
            <v-row 
                dense 
                class="d-flex justify-end pa-0"
            >
                <AugmentedReality
                    v-if="arAvailable && data.arUrl"
                    class="pt-5 pr-5 pb-3"
                />
                <LightVariation
                    v-if="lightVariationsList.length"
                    class="pt-5 pr-5 pb-3"  
                    :lightVariationsList="lightVariationsList"
                    @select-variation="selectVariation($event)"
                />
            </v-row>
        </v-container>

        <div
            ref="generalHolder"
            class="content-widget digital-model"
            @click.prevent="antiScroll()"
        />
        <EditDigitalModel
            v-if="canBeEditable"
            :active="active"
            :data="data"
            :link="link"
            :editProperties="editProperties"
            :defaultProperties="defaultProperties"
        />
        <ScrollButton
            v-if="isIframe"
            :toggleModeWheel="toggleModeWheel"
            :options="data"
        />
        <ThreeDController
            v-if="data.gui && data.gui.type === 'general'"
            :options="data"
            :widget="widget"
            :targetElement="targetElement"
            :canBeEditable="canBeEditable"
            :useCompass="useCompass"
            :widgetType="'digital-model'"
            :loadedWidgets="loadedWidgets"
        />
        <PopupInformation
            :data="popup"
            :update="updateWidget"
            :target="targetElement"
            :popupType="'hotspot'"
        />
        <LoaderProgressBar v-if="isLoadingVariation" />
    </div>
</template>

<script>
    import _ from 'lodash';
    import { EventBus } from '@/event-bus';
    import Http from '@/api/data';
    import { mapActions, mapGetters } from 'vuex';

    import EditDigitalModel from '@/components/navbarLayout/editMode/EditDigitalModel';
    import ThreeDController from '@/components/threeDController/ThreeDController';
    import ScrollButton from '@/components/ScrollButton';
    import PopupInformation from '@/components/plotInformation/PopupInformation';
    import AugmentedReality from '@/components/augmentedReality/AugmentedReality';
    import LightVariation from '@/components/lightVariation/LightVariation';
    import LoaderProgressBar from '@/components/LoaderProgressBar';

    export default {
        name: 'DigitalModelWidget',
        components: {
            AugmentedReality,
            EditDigitalModel,
            ThreeDController,
            ScrollButton,
            PopupInformation,
            LightVariation,
            LoaderProgressBar,
        },
        props: {
            data: {
                type: Object,
                required: true
            },
            mode: {
                type: String,
                required: false
            },
            active: {
                type: Boolean,
                required: true
            },
            link: {
                type: String,
                required: false
            },
            standalone: {
                type: Boolean,
                required: true
            },
            widgetResolutions: {
                type: Object,
                required: true
            },
            defaultWidgetTitle: {
                type: String,
                required: false
            },
            loadedWidgets: {
                type: Array,
                required: true
            }
        },
        data: () => ({
            targetElement: null,
            widget: null,
            compass: null,
            currentViews: null,
            popup: {
                x: 0,
                y: 0,
                visible: false,
            },
            editProperties: {
                currentAngle: null,
                currentZoom: null,
                currentCompass: null,
            },
            defaultProperties: {
                currentAngle: null,
                currentZoom: null,
                currentCompass: null,
            },
            firstScale: null,
            toggleModeWheel: false,
            useCompass: null,
            compassData: null,
            currentLink: null,
            updateMetadata: false,
            lightVariationsList: [],
            isLoadingVariation: false,
            compassWidget: null
        }),
        computed: {
            ...mapGetters([
                'isIframe',
                'isInternal',
                'isOfflineMode',
                'isOnline',
                'isReviewUse',
                'protocol',
                'programInfo',
                'language',
                'resolution'
            ]),
            isEditable() {
                return this.isInternal ? this.$route.query.edit !== undefined : false;
            },
            canBeEditable() {
                if (this.standalone && this.isEditable) return true;
                return false;
            },
            arAvailable() {
                return this.$vuetify.breakpoint.mdAndDown && !this.isOfflineMode && window.self === window.top;
            },
            hasSharedFavlist() {
                return this.$route.query.favlist ? true : false;
            },
            apiUrl() {
                return this.protocol + (this.isReviewUse ? process.env.API_LIST.REVIEW : process.env.API_LIST.FO);
            },
        },
        async mounted() {
            this.isLoadingVariation = true;
            this.targetElement = this.$refs.generalHolder;
            this.updateWidget = this.updateWidget.bind(this);
            this.render();
            EventBus.$on('generalView_SetActive', (item) => {
                this.setActive(item);
            });

            EventBus.$on('compassWidget', (widgetType, compassInstance) => {
                if (widgetType === 'digital-model' && this.$props.data.compass) {
                    this.compassWidget = compassInstance;
                    this.compassWidget.refresh(this.$props.data.startIndex, this.$props.data.images[0].length);
                }
            });
        },
        methods: {
            ...mapActions(['setIsLoading', 'setLogo', 'setProgramInfo']),
            ...mapActions('widget', ['setWidgetScene']),
            setActive(item) {
                let localData = _.cloneDeep(this.data);
                let widgetType = item.id;
                let widgetId;
                localData.widgetType = widgetType;
                this.resetActiveHotspots(localData.levels);
                this.setWidgetScene(null);

                localData.levels.filter((widget) => widget.id === item.id)[0].active = true;
                switch (item.id) {
                    case 'cutaway':
                    case 'drone-view':
                    case 'plot-grid':
                        widgetId = item.id;
                        break;

                    default:
                        widgetId = 'digital-model';
                        break;
                }

                if (item.id !== 'cutaway') {
                    localData.widgetTitle = this.$props.defaultWidgetTitle;
                }

                EventBus.$emit('updateSidebar', localData, true);

                EventBus.$emit('updateLoadedWidgets', {
                    id: widgetId,
                    link: item.link,
                    active: true,
                    widgetType: widgetType,
                });
            },
            resetActiveHotspots(widgetList) {
                widgetList.forEach((el) => {
                    el.active = false;
                    if (el.hotspots) el.hotspots.forEach(spot => (spot.active = false));
                });
            },
            callbacks() {
                let vm = this;
                return {
                    callbacks: {
                        overCallback(hotspot, x, y) {
                            if (vm.$vuetify.breakpoint.lgAndUp) vm.popup = Object.assign(hotspot, { x: x, y: y });
                        },
                        outCallback() {
                            vm.popup = { x: 0, y: 0 };
                        },
                        clickCallback(hotspot, x, y) {
                            vm.popup = Object.assign(hotspot, { x: x, y: y });
                        },
                        widgetReadyCallback(widget) {
                            EventBus.$emit('widgetReady', widget);
                        },
                        indexCallback(currentIndex, currentViews) {
                            vm.editProperties.currentAngle = currentIndex;
                            vm.currentViews = currentViews;
                            if (vm.useCompass && vm.compassWidget) {
                                vm.compassWidget.refresh(currentIndex, currentViews);
                            }
                        },
                        zoomCallback(currentScale) {
                            vm.editProperties.currentZoom = currentScale / vm.firstScale;
                        },
                        scaleCallback(scale) {
                            vm.firstScale = scale / vm.editProperties.currentZoom;
                        },
                        imageReadyCallback(image) {
                            EventBus.$emit('stopLoader');
                            vm.defaultProperties = Object.assign({}, vm.editProperties);
                            vm.isLoadingVariation = false;
                            vm.setIsLoading(false);
                        },
                    },
                };
            },
            getUrlLink() {
                let url = null;
                switch (this.mode) {
                    case 'general':
                        url = this.apiUrl + '/request-data/general-view' + window.location.search;
                        break;

                    case 'digital-model':
                        url = this.apiUrl + '/request-data/digital-model' + window.location.search;
                        break;

                    case 'ground-plan':
                        url = this.apiUrl + '/request-data/ground-plan' + window.location.search;
                        break;

                    default:
                        break;
                }
                return url;
            },
            getAPIUrl() {
                let urlLink = this.$props.link ? this.$props.link : this.getUrlLink();
                if (this.language && !urlLink.includes('&language')) urlLink += '&language=' + this.language;
                if (this.isOfflineMode && !urlLink.includes('&offline')) urlLink += '&offline';
                if (this.resolution === '4k' && !urlLink.includes('&resolution')) urlLink += '&resolution=' + this.resolution;
                return urlLink;
            },
            async render() {
                this.isLoadingVariation = true;
                this.currentLink = this.getAPIUrl();
                await Http.getData(this.currentLink)
                    .then((payload) => {
                        if (payload) {
                            payload.gui.type = 'general';
                            if (payload.language) this.$i18n.locale = payload.language;

                            if (this.standalone) payload.type = 'general';

                            payload = Object.assign(payload, this.callbacks());

                            // Remove virtual-tour hotspots if offline mode
                            if (payload.hasOwnProperty('hotspots') && !this.isOnline && this.isOfflineMode)
                                payload.hotspots = payload.hotspots.filter((hotspot) => hotspot.type !== 'virtual-tour');

                            if (payload.startIndex) this.defaultProperties.currentAngle = parseInt(payload.startIndex);

                            if (payload.zoomFactor) this.defaultProperties.currentZoom = payload.zoomFactor;

                            if (payload.compass) {
                                this.useCompass = payload.compass.active || this.canBeEditable;
                                this.defaultProperties.currentCompass = parseInt(payload.compass.index);
                                if (this.canBeEditable) {
                                    payload.compass.active = true;
                                }
                            }

                            this.editProperties = Object.assign({}, this.defaultProperties);

                            if (payload.private && payload.private.arURL) payload.arUrl = payload.private.arURL;
                            else payload.arUrl = null;

                            const activeWidget = this.$props.loadedWidgets.find((widget) => widget.active === true);

                            // Concat "widget type" with "digital-model ID" in order to be able to persist more than one digital-model for each type
                            let digitalModelUrl = new URL(this.currentLink);
                            let digitalModelParam = new URLSearchParams(digitalModelUrl.search);
                            payload.widgetType = `${activeWidget.widgetType}-${digitalModelParam.get('id')}`;

                            payload.startLevel = 0;

                            if (this.widget) {
                                this.widget.loadData(payload);
                            } else {
                                this.widget = new Habiteo.DigitalModelWidget(this.$refs.generalHolder, payload);
                            }

                            let widget = payload.levels ? payload.levels.filter((level) => level.active === true) : null;
                            let widgetId = widget && widget.map((match) => match.id) ? widget.map((match) => match.id)[0] : null;
                            payload.widgetType = widgetId;

                            if (this.standalone) {
                                if (widgetId === 'drone-view') {
                                    this.currentLink = widget.map((match) => match.link) ? widget.map((match) => match.link)[0] : this.$props.link;
                                }
                                EventBus.$emit('updateLoadedWidgets', {
                                    id: widgetId === 'drone-view' ? 'drone-view' : 'digital-model',
                                    link: this.currentLink,
                                    active: true,
                                    widgetType: widgetId,
                                });
                            }

                            EventBus.$emit('updateSidebar', payload, false);
                            EventBus.$emit('trackWidget', payload.tracking);

                            // Persist Program logo into store
                            if ((!this.logos && 'program' in payload) || ('program' in payload && this.logos && !('program' in this.logos))) {
                                if (payload.program.logo) {
                                    this.setLogo({
                                        type: 'program',
                                        src: payload.program.logo,
                                    });
                                }
                            }

                            // Persist promoter logo into store
                            if (
                                (!this.logos && 'promoter' in payload) ||
                                ('promoter' in payload && this.logos && !('promoter' in this.logos))
                            )
                                if (payload.promoter.logo) {
                                    this.setLogo({
                                        type: 'promoter',
                                        src: payload.promoter.logo,
                                    });
                                }

                            // Persist program info into store
                            if (!this.programInfoLink && 'program' in payload) {
                                if (payload.program.details) {
                                    Http.getData(payload.program.details).then((programInfoData) => {
                                        if (programInfoData.length) {
                                            this.setProgramInfo(programInfoData[0]);
                                        }
                                    });
                                }
                            }

                            if (this.hasSharedFavlist) {
                                EventBus.$emit('saveSidebar');

                                payload.levels.forEach((level) => {
                                    level.active = ['cutaway', 'plot-grid'].includes(level.id);
                                });

                                EventBus.$emit('updateLoadedWidgets', {
                                    id: 'plot-grid',
                                    link: this.apiUrl + '/request-data/cutaway-view' + window.location.search,
                                    active: true,
                                    widgetType: 'plot-grid',
                                });

                                EventBus.$emit('updateSidebar', payload, false);
                            }
                            this.setLightVariationList(payload);
                        }
                    }).catch((error) => {
                        EventBus.$emit('setErrorState', true, error);
                    });
            },
            async updateData() {
                this.isLoadingVariation = true;
                this.currentLink = this.getAPIUrl();

                const activeWidget = this.loadedWidgets.find((widget) => widget.active === true);
                await Http.getData(this.currentLink).then((payload) => {
                    if (payload) {
                        if (!payload.gui) {
                            payload.gui = {};
                        }
                        payload.gui.type = 'general';
                        if (!payload.resolution) {
                            payload.resolution = this.resolution;
                        }

                        // Remove virtual-tour hotspots if offline mode
                        if (payload.hasOwnProperty('hotspots') && !this.isOnline && this.isOfflineMode) {
                            payload.hotspots = payload.hotspots.filter((hotspot) => hotspot.type !== 'virtual-tour');
                        }

                        if (payload.startIndex) {
                            this.defaultProperties.currentAngle = parseInt(payload.startIndex);
                        }

                        if (payload.zoomFactor) {
                            this.defaultProperties.currentZoom = payload.zoomFactor;
                        }

                        if (payload.compass) {
                            this.defaultProperties.currentCompass = parseInt(payload.compass.index);
                            this.useCompass = payload.compass.active || this.canBeEditable;
                            if (this.canBeEditable) {
                                payload.compass.active = true;
                            }
                            if (this.compassData) {
                                this.compassData.indexNorth = this.defaultProperties.currentCompass;
                            }
                        }

                        payload.arUrl = null;
                        if (payload.private && payload.private.arURL) {
                            payload.arUrl = payload.private.arURL;
                        } else {
                            payload.arUrl = null;
                        }

                        // Concat "widget type" with "digital-model ID" in order to be able to persist more than one digital-model for each type
                        let digitalModelUrl = new URL(this.currentLink);
                        let digitalModelParam = new URLSearchParams(digitalModelUrl.search);
                        payload.widgetType = `${activeWidget.widgetType}-${digitalModelParam.get('id')}`;
                        payload.startLevel = 0;

                        if (this.updateMetadata) {
                            payload.resolution = this.resolution;
                            payload.startIndex = this.defaultProperties.currentAngle;
                            payload.zoomFactor = this.defaultProperties.currentZoom;
                            this.updateMetadata = false;
                        } else {
                            this.editProperties = Object.assign({}, this.defaultProperties);
                        }

                        payload = Object.assign(this.data, payload, this.callbacks());
                        this.widget.loadData(payload);
                        EventBus.$emit('updateSidebar', payload, false);
                        EventBus.$emit('trackWidget', payload.tracking);

                        this.setLightVariationList(payload);
                        if (this.currentVariationName) {
                            this.loadLightVariation(this.currentVariationName);
                        }
                    }
                })
                .catch((error) => {
                    EventBus.$emit('setErrorState', true, error);
                });
            },
            updateWidget(item) {
                // 'type' is not exist for interactive zone object
                if (!('type' in item) || item.type === 'plan-3d') {
                    EventBus.$emit('updateSidebar', {
                        type: 'plot',
                        activeHotspot: item,
                    });
                    EventBus.$emit('updateLoadedWidgets', {
                        id: 'plot',
                        link: item.link,
                        active: true,
                        widgetType: item.type,
                    });
                } else {
                    switch (item.type) {
                        case 'digital-model':
                            this.setIsLoading(true);

                            EventBus.$emit('updateSidebar', {
                                type: 'general',
                                activeHotspot: item,
                            },
                            true);

                            EventBus.$emit('updateLoadedWidgets', {
                                id: 'digital-model',
                                link: item.link,
                                active: true,
                                widgetType: item.id,
                            });
                            break;

                        default:
                            EventBus.$emit('updateSidebar', {
                                type: item.type,
                                activeHotspot: item,
                            });
                            EventBus.$emit('updateLoadedWidgets', {
                                id: item.type,
                                link: item.link,
                                active: true,
                                widgetType: item.type,
                            });
                            break;
                    }
                }
            },
            selectVariation(variation) {
                this.isLoadingVariation = true;
                const activeWidget = this.loadedWidgets.find((widget) => widget.active === true);
                this.lightVariationsList.forEach((variation) => (variation.active = false));
                let apiUrl = this.currentLink;
                if (variation) {
                    this.$set(variation, 'active', true);
                    const indexActive = this.lightVariationsList.findIndex((lightVariation) => lightVariation.id === variation.id);
                    this.$set(this.lightVariationsList, indexActive, variation);
                    this.currentVariationName = variation.name;

                    let variationLink = new URL(variation.link);
                    if (this.resolution && this.resolution !== '720p') variationLink.searchParams.set('resolution', this.resolution);
                    else variationLink.searchParams.delete('resolution');
                    apiUrl = variationLink;
                } else {
                    this.lightVariationsList = Object.assign([], this.lightVariationsList);
                }

                Http.getData(apiUrl).then((payload) => {
                    // Concat "widget type" with "digital-model ID" and "Variation name" (if filled) in order to be able to persist more than one digital-model for each type
                    let digitalModelUrl = new URL(this.currentLink);
                    let digitalModelParam = new URLSearchParams(digitalModelUrl.search);

                    payload = Object.assign(payload, this.callbacks());
                    payload.startIndex = this.editProperties.currentAngle;
                    payload.widgetType = `${activeWidget.widgetType}-${digitalModelParam.get('id')}`;
                    payload.resolution = this.resolution;
                    if (variation) {
                        payload.widgetType += `-${variation.name}`;
                    }
                    this.widget.loadData(payload);
                });
            },
            triggerResize() {
                window.dispatchEvent(new Event('resize'));
            },
            antiScroll() {
                if (this.widget) this.widget.unlockMouseWheel();
                this.toggleModeWheel = !this.toggleModeWheel;
            },
            loadLightVariation(variationName) {
                let hasLevels = 'levels' in this.$props.data;
                let widgetList = hasLevels ? this.$props.data.levels : this.$props.data.items;
                const activeWidget = this.loadedWidgets.find((widget) => widget.active === true);
                let currentWidget = widgetList ? widgetList.find((widget) => widget.id === activeWidget.widgetType) : null;
                let currentVariation = currentWidget.hotspots.find((hotspot) => hotspot.name === variationName);
                if (currentVariation) this.selectVariation(currentVariation);
            },
            setLightVariationList(payload) {
                let widgetList = this.$props.data.type && this.$props.data.type === 'program' ? this.$props.data.widgetList : payload.levels;
                let activeWidget = widgetList ? widgetList.find((widget) => widget.active) : null;
                let hotspotList = activeWidget ? activeWidget.hotspots : null;
                this.lightVariationsList = hotspotList ? hotspotList.filter((hotspot) => hotspot.type === 'light-variation') : [];
            },
        },
        watch: {
            active(val) {
                if (val) {
                    this.updateData();
                    this.triggerResize();
                }
            },
            link() {
                if (this.active && this.currentLink !== this.link) {
                    this.updateMetadata = true;
                    this.updateData();
                    this.triggerResize();
                }
            }
        }
    };
</script>

<style lang="scss">
    $canvas-animation: transform .25s;
    .digital-header {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 2;
    }

    .content-widget {
        &.digital-model {
            margin-left: 0;
            transition: margin .5s ease;
            > div, 
            > div * {
                transition: $canvas-animation ;
                -moz-transition: $canvas-animation ; 
                -webkit-transition: $canvas-animation ; 
                -o-transition: $canvas-animation ; 
                -ms-transition: $canvas-animation ; 
            }
            text-align: left;
        }
    }
</style>


// WEBPACK FOOTER //
// src/views/DigitalModelWidget.vue